import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@silospot/i18n/react';
import { passwordSchema } from '@silospot/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  toast,
} from '@silospot/ui';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { api } from '../lib/api';

const formSchema = z.object({ password: passwordSchema });
type FormValues = z.infer<typeof formSchema>;

export function ResetPasswordPage(): ReactElement {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: '' },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      api.confirmPasswordReset({ token, password: values.password }),
    onError: () => toast.error(t('errors.generic')),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('resetPassword.title')}</CardTitle>
        {!token ? <CardDescription>{t('errors.generic')}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        {mutation.isSuccess ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('resetPassword.success')}</p>
            <Link to="/login" className="text-sm text-primary hover:underline">
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('resetPassword.passwordLabel')}</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={mutation.isPending || !token}>
                {t('resetPassword.submit')}
              </Button>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
}
